import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UnitDefinition, UnitsResponse } from "@src/types/units";

export interface UnitsState {
  /** Units catalog keyed by unit_id — the id widgets store in presentation.unit. */
  byUnitId: Record<string, UnitDefinition>;
  status: "idle" | "loading" | "success" | "error";
  error?: string;
}

const initialState: UnitsState = {
  byUnitId: {},
  status: "idle",
};

// Loaded once at dashboards-load so the drawer + widgets resolve unit ids
// without re-fetching the units endpoint.
const unitsSlice = createSlice({
  name: "units",
  initialState,
  reducers: {
    unitsRequested(state) {
      state.status = "loading";
    },
    unitsLoaded(state, action: PayloadAction<UnitsResponse>) {
      const byUnitId: Record<string, UnitDefinition> = {};
      // Key strictly by unit_id — the only identifier widgets store in
      // presentation.unit. Storing the endpoint's own key too would duplicate
      // entries in the drawer's dropdown (Object.entries(units)).
      Object.values(action.payload).forEach((unit) => {
        if (!byUnitId[unit.unit_id]) byUnitId[unit.unit_id] = unit;
      });
      state.byUnitId = byUnitId;
      state.status = "success";
      state.error = undefined;
    },
    unitsFailed(state, action: PayloadAction<string>) {
      state.status = "error";
      state.error = action.payload;
    },
  },
});

export const { unitsRequested, unitsLoaded, unitsFailed } = unitsSlice.actions;

// Structural state param avoids a store→index→slice import cycle.
export const selectUnitsById = (state: { units: UnitsState }) =>
  state.units.byUnitId;
export const selectUnitsStatus = (state: { units: UnitsState }) =>
  state.units.status;

export default unitsSlice.reducer;
